// The shared alarm authoring form (UI-3) — used both to CREATE (from a library
// recipe) and to EDIT an existing definition. All draft state + validation is
// the pure `alarmDefsForm` reducer; this component is the thin controlled-input
// shell over it. The parent owns the network call: it receives the finished
// draft via `onSave` and builds the create/patch body (so the minimal-PATCH
// diff stays in one tested place). Threshold values start blank on create — the
// wire library carries bounds, not recommended numbers, so the operator sets a
// deliberate value rather than inheriting a fabricated (and possibly always-
// firing) default.

import type { UnitSettings, VesselType } from '@neomaris/protocol';
import { useMemo, useState } from 'react';

import {
  bindingChoices,
  canSplitByMode,
  engineConditionIsRequired,
  hasErrors,
  isSplitByMode,
  likelyInstanceHint,
  modeValue,
  perModeEvaluationNotice,
  pinCopyFor,
  setBinding,
  setEnabledModes,
  setEngineCondition,
  setMinRpm,
  setSeverity,
  setSplitByMode,
  setThreshold,
  setTitle,
  contactStateClearable,
  isContactStateParam,
  sharedValue,
  splitCollapseLoses,
  toggleMode,
  validate,
  type AlarmDraft,
} from '../../lib/alarmDefsForm';
import { observationText, type LiveObservation } from '../../lib/alarmLive';
import { conditionsClause, convertedQuantity } from '../../lib/alarmSentence';
import {
  formatEditedNumber,
  paramEditorUnits,
  paramLiveDomain,
  pathFamilyMatcher,
  type ParamEditorUnits,
  type RecipeParamMeta,
} from '../../lib/alertTemplates';
import { useSignalCatalog } from '../../lib/useSignalCatalog';
import { useBoatChrome } from '../app/boatChrome';
import { InfoCircleIcon } from '../app/icons';
import { ContactStateSelect } from './ContactStateSelect';
import { SeveritySelect, WatchScheduleField } from './formFields';

interface AlarmDefinitionFormProps {
  mode: 'create' | 'edit';
  draft: AlarmDraft;
  onDraftChange: (draft: AlarmDraft) => void;
  /** Called with a validated draft; the parent builds + sends the body. */
  onSave: (draft: AlarmDraft) => void;
  onCancel: () => void;
  /** True while the parent's network call is in flight. */
  busy: boolean;
  /** A server-side error (honest inline banner), or null. */
  serverError: string | null;
  submitLabel: string;
  pendingLabel: string;
  /**
   * What this alarm watches TODAY, as the compiler resolved it (ADR 0170). Only
   * the detail page can supply it: on `/alarms/new` there is no compiled rule
   * yet, so the reading follows the pin the operator picks — or is simply
   * absent, which is the honest state of an alarm that does not exist.
   *
   * A pin in the draft always WINS over this, because the pin is what the next
   * compile will bind and the reading has to describe the alarm being saved, not
   * the one that was.
   */
  resolvedPath?: string | null;
  /** Per-path reading, from the page's `observationLookup` (`alarmLive`). */
  observationFor?: (path: string) => LiveObservation;
  /** The page's clock, so the "now" wording ages with everything else on it. */
  now?: number;
  /**
   * What kind of boat this is (ADR 0173), or null when unknown — passed through
   * to the watch-schedule field, which is the only thing on this form that
   * speaks about sailing.
   */
  vesselType?: VesselType | null;
}

/**
 * The live reading of the watched signal, in the unit of ONE threshold box —
 * "now 26.4 V", "now 98 kPa", "now 203.0°F" — or `null` when it must not be
 * shown.
 *
 * Withheld five ways, all of them the same rule: never put a number here that
 * the owner cannot compare with the one they are typing.
 *
 *  - the param is not a reading at all (a delay, a count) — {@link paramLiveDomain};
 *  - a converted param whose signal declares some OTHER unit than the store unit
 *    the conversion assumes: scaling that by ten would invent a pressure;
 *  - a box the ACCOUNT's units moved (ADR 0189) whose reading cannot make the
 *    same move — see below;
 *  - nothing numeric to show — no reading yet, or a switch state under a volts
 *    box. A form field stays quiet about that; the detail panel is where an
 *    absent reading is stated in words.
 *  - no observation source at all (the create page before a pin is chosen).
 *
 * WHICH of the two conversions applies is {@link paramLiveDomain}'s call, not
 * this function's — it is asked with the same account settings the box was
 * rendered from, so the hint and the box cannot disagree about whether this
 * param moved. This file only renders the answer.
 *
 * On the account arm the conversion is keyed off the unit the BOAT declared for
 * the signal, not off the param's registry unit, because that is what the
 * reading is actually in — and if the boat declares something the category's
 * store-unit gate refuses (or declares nothing at all), the number cannot be
 * moved and the line is withheld rather than printed in metres under a box
 * reading feet. `convertedQuantity` is that gate, asked directly.
 *
 * Worded by {@link observationText}, lower-cased to sit under the field, so a
 * stale value carries its age here exactly as it does on the detail panel.
 */
function paramReadingHint(
  param: RecipeParamMeta,
  observation: LiveObservation | undefined,
  observedUnit: string | null | undefined,
  now: number,
  settings: UnitSettings | undefined
): string | null {
  if (observation === undefined) return null;
  const domain = paramLiveDomain(param, settings);
  if (domain === null) return null;
  if (typeof observation.value !== 'number') return null;
  const declared = typeof observedUnit === 'string' ? observedUnit.trim() : '';
  if (domain.kind === 'account') {
    if (declared.length === 0) return null;
    if (
      convertedQuantity(observation.value, declared, domain.category, domain.settings) === undefined
    ) {
      return null;
    }
    // The raw reading goes down, not a pre-scaled one: `displayUnits` owns the
    // affine step AND the precision that goes with the unit it lands in.
    return lowerFirst(
      observationText(observation, declared, now, domain.category, domain.settings)
    );
  }
  if (
    domain.storeUnit !== undefined &&
    declared.length > 0 &&
    declared.toLowerCase() !== domain.storeUnit.toLowerCase()
  ) {
    return null;
  }
  const converted: LiveObservation = { ...observation, value: observation.value * domain.factor };
  return lowerFirst(observationText(converted, domain.unitSuffix, now));
}

/** A sentence-cased phrase re-cased to sit mid-line under a field. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * The bounds beside a legend, IN THE UNIT OF THE BOX BELOW IT — "0–3600
 * seconds", never "0–3600000 ms". Both numbers and the suffix come from the one
 * resolver the input itself uses, so the hint cannot drift from the field it
 * describes.
 */
function boundsHint(units: ParamEditorUnits): string {
  const parts: string[] = [];
  if (units.min !== undefined && units.max !== undefined) {
    parts.push(`${formatEditedNumber(units.min)}–${formatEditedNumber(units.max)}`);
  } else if (units.min !== undefined) {
    parts.push(`≥ ${formatEditedNumber(units.min)}`);
  } else if (units.max !== undefined) {
    parts.push(`≤ ${formatEditedNumber(units.max)}`);
  }
  if (units.unitSuffix.length > 0) parts.push(units.unitSuffix);
  return parts.join(' ');
}

export function AlarmDefinitionForm({
  mode,
  draft,
  onDraftChange,
  onSave,
  onCancel,
  busy,
  serverError,
  submitLabel,
  pendingLabel,
  resolvedPath,
  observationFor,
  now,
  vesselType,
}: AlarmDefinitionFormProps) {
  // Errors surface only after a submit attempt (don't shout on an untouched form).
  const [submitted, setSubmitted] = useState(false);
  const errors = useMemo(() => validate(draft, { requireTitle: mode === 'edit' }), [draft, mode]);
  const showErrors = submitted;
  // v1's shared compiler evaluates only the primary mode's thresholds; disclose
  // it when two-or-more enabled modes carry distinct complete blocks.
  const perModeNotice = useMemo(() => perModeEvaluationNotice(draft), [draft]);
  // Whether the engine clause is one the alarm CANNOT fire without — decides
  // which honesty note the "Only when" section may state.
  const engineConditionExclusive = engineConditionIsRequired(draft);

  // Which instance this alarm watches (`binding.path`, ADR-097 §4). The catalog
  // is fetched here rather than passed in so BOTH surfaces that render this form
  // — the new-alarm page and the detail page's inline editor — ask the question
  // the same way, with no prop to forget. It is the same accepted-path set the
  // compiler binds against, so the options can only be paths that will compile.
  //
  // Gated on a READY catalog: a list we could not load must not be read as "the
  // boat reports nothing", which would flag a perfectly healthy stored pin as
  // "not currently reported". While loading or on error the select is simply
  // absent, and the untouched draft carries the stored pin through the save.
  const { boatId } = useBoatChrome();
  const catalog = useSignalCatalog(boatId);
  const familyMatches = useMemo(
    () => pathFamilyMatcher(draft.bindingFamily),
    [draft.bindingFamily]
  );
  // Whether a pin is MANDATORY here (core ADR-116 part A) and, if we have
  // something useful to guess with, how to sort and label the guesses.
  const pinRequired = draft.bindingRequired === true;
  const instanceHint = useMemo(() => likelyInstanceHint(draft.recipeId), [draft.recipeId]);
  const instanceChoices = useMemo(
    () =>
      catalog.status === 'ready'
        ? bindingChoices(catalog.signals, familyMatches, draft.binding, {
            required: pinRequired,
            ...(instanceHint !== undefined ? { hint: instanceHint } : {}),
          })
        : [],
    [catalog.status, catalog.signals, familyMatches, draft.binding, pinRequired, instanceHint]
  );
  // The stored pin is one the compiler cannot bind — the alarm is sitting
  // compile-pending on it, so say so (and say WHICH problem it is) instead of
  // leaving a selected option that looks like every other one.
  const pinProblem = instanceChoices.find((c) => c.path === draft.binding)?.problem;
  // Split for the two <optgroup>s. Both halves are always rendered when the
  // hint applies — the "other" half is SECOND, never absent. On a boat whose
  // installer labelled nothing every channel lands in it, and the owner picks
  // from an unhinted list, which is exactly right: they know the wiring.
  const likelyChoices = instanceChoices.filter((c) => c.likely === true);
  const otherChoices = instanceChoices.filter((c) => c.likely !== true);
  const grouped = instanceHint !== undefined && likelyChoices.length > 0;

  // The signal every threshold box below is a number ON, so each can show what
  // it reads right now (ADR 0170). The DRAFT's pin wins over the compiler's
  // current resolution: the pin is what the next compile binds, so a reading has
  // to describe the alarm about to be saved rather than the one that was. With
  // neither — an unpinned alarm on the new-alarm page — there is nothing to read
  // and no hint is shown.
  const pinnedPath = draft.binding.trim();
  const watchedPath = pinnedPath.length > 0 ? pinnedPath : (resolvedPath ?? '');
  const watchedSignal = useMemo(
    () => (watchedPath === '' ? undefined : catalog.signals.find((s) => s.path === watchedPath)),
    [catalog.signals, watchedPath]
  );
  const watchedObservation =
    watchedPath === '' || observationFor === undefined ? undefined : observationFor(watchedPath);
  const readingNow = now ?? Date.now();

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setSubmitted(true);
    if (hasErrors(errors)) return;
    onSave(draft);
  }

  return (
    <form className="alarm-form" data-testid="alarm-def-form" onSubmit={submit} noValidate>
      <div className="form-group">
        <label htmlFor="alarm-form-title">Name</label>
        <input
          id="alarm-form-title"
          className="form-input"
          type="text"
          value={draft.title}
          placeholder={mode === 'create' ? 'Use the recipe name' : undefined}
          data-testid="alarm-form-title"
          onChange={(e) => onDraftChange(setTitle(draft, e.target.value))}
        />
        {showErrors && errors.title ? <p className="alarm-form-err">{errors.title}</p> : null}
      </div>

      {/* Which instance to watch. Shown when there is a real choice — two or
          more reported paths in the recipe's family, or an alarm that already
          carries a pin. One instrument and no pin renders nothing: a select with
          a single option is clutter, and "Automatic" already means that one.

          A PIN-MANDATORY recipe (core ADR-116 part A) is the exception on both
          counts: it renders even for one option, and it offers no "Automatic".
          `bilge_high`'s family is every switch channel aboard, so "whichever one
          your boat reports first" is as likely to be the cabin lights as the
          float switch — and the API refuses an unpinned one anyway. */}
      {instanceChoices.length > 0 ? (
        <div className="form-group">
          <label htmlFor="alarm-form-binding">
            {pinRequired ? pinCopyFor(draft.recipeId).question : 'Which one should it watch?'}
          </label>
          <select
            id="alarm-form-binding"
            className="form-input"
            value={draft.binding}
            data-testid="alarm-form-binding"
            onChange={(e) => onDraftChange(setBinding(draft, e.target.value))}
          >
            {pinRequired ? (
              // A placeholder, not a choice. `validate` refuses the empty value,
              // so this can never be saved — it exists so the select opens on
              // "unanswered" rather than silently pre-selecting a channel for
              // someone.
              <option value="">Choose a channel…</option>
            ) : (
              <option value="">Automatic (first reported)</option>
            )}
            {grouped ? (
              <>
                <optgroup label={instanceHint!.likelyGroup}>
                  {likelyChoices.map((choice) => (
                    <option
                      key={choice.path}
                      value={choice.path}
                      data-testid={`alarm-form-binding-option-${choice.path}`}
                    >
                      {choice.label}
                    </option>
                  ))}
                </optgroup>
                {/* NEVER omitted. Every channel in the family stays choosable
                    even when nothing about its name suggests a bilge — an
                    unlabelled channel is the common case on a boat whose
                    installer typed nothing, and the owner knows the wiring. */}
                {otherChoices.length > 0 ? (
                  <optgroup label={instanceHint!.otherGroup}>
                    {otherChoices.map((choice) => (
                      <option
                        key={choice.path}
                        value={choice.path}
                        data-testid={`alarm-form-binding-option-${choice.path}`}
                      >
                        {choice.label}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </>
            ) : (
              instanceChoices.map((choice) => (
                <option
                  key={choice.path}
                  value={choice.path}
                  data-testid={`alarm-form-binding-option-${choice.path}`}
                >
                  {choice.label}
                </option>
              ))
            )}
          </select>
          {showErrors && errors.binding !== undefined ? (
            <p className="alarm-form-err" data-testid="alarm-form-binding-required">
              {errors.binding}
            </p>
          ) : null}
          {pinProblem !== undefined ? (
            <p className="alarm-form-err" data-testid="alarm-form-binding-missing">
              {pinProblem === 'not-reported'
                ? pinRequired
                  ? 'Your boat isn’t reporting this channel right now, so the alarm is waiting for it rather than watching another. Pick one that is reporting.'
                  : 'Your boat isn’t reporting this one right now, so the alarm is waiting for it rather than watching another. Pick one that is reporting, or switch to Automatic.'
                : pinRequired
                  ? 'This alarm can’t watch the channel it’s set to, so it isn’t running. Pick one from the list.'
                  : 'This alarm can’t watch the one it’s set to, so it isn’t running. Pick one from the list, or switch to Automatic.'}
            </p>
          ) : pinRequired ? (
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {pinCopyFor(draft.recipeId).explainer}
            </p>
          ) : (
            <p className="muted" style={{ margin: '6px 0 0' }}>
              Automatic watches whichever one your boat reports first. Pick a specific one to keep
              this alarm on it — if that one stops reporting, the alarm waits for it instead of
              quietly switching to another.
            </p>
          )}
        </div>
      ) : null}

      <SeveritySelect
        value={draft.severity}
        onChange={(severity) => onDraftChange(setSeverity(draft, severity))}
      />

      <WatchScheduleField
        name="alarm-form-watch"
        modeUniverse={draft.modeUniverse}
        enabledModes={draft.enabledModes}
        onSetModes={(modes) => onDraftChange(setEnabledModes(draft, modes))}
        onToggleMode={(m) => onDraftChange(toggleMode(draft, m))}
        error={showErrors ? errors.modes : undefined}
        vesselType={vesselType}
      />

      {/* One number per setting, unless the operator asks for more. The recipe
          metadata's `perMode` flag says the split is ALLOWED; it does not say it
          is wanted, and reading it as an instruction opened this form with ten
          boxes on a five-mode recipe. See `AlarmDraft.splitModes`. */}
      {draft.params.map((param) => {
        const split = isSplitByMode(draft, param.key);
        const canSplit = canSplitByMode(draft, param.key);
        const wouldLose = split ? splitCollapseLoses(draft, param.key) : null;
        // The owner's unit for a param the registry marks `display` (a delay in
        // seconds, a tank level in percent) or that the ACCOUNT has its own unit
        // for (ADR 0189), the stored one for everything else. The draft already
        // holds edited values, so the inputs need no conversion here — see the
        // units note in `alarmDefsForm`.
        //
        // Resolved from the DRAFT's units, never from `useUnits()` directly:
        // those are the units the boxes were filled in, and the baseline that
        // makes an untouched save re-emit its stored number was captured against
        // them. Re-reading the account here would let a preference changed in
        // another tab relabel a box whose contents were rendered in the old unit.
        const units = paramEditorUnits(param, draft.units);
        const hint = boundsHint(units);
        // What the watched signal reads, restated in THIS box's unit — or null
        // when the two are not comparable. See `paramReadingHint`.
        const readingHint = paramReadingHint(
          param,
          watchedObservation,
          watchedSignal?.unit,
          readingNow,
          draft.units
        );
        return (
          <fieldset key={param.key} className="alarm-form-fieldset">
            <legend>
              {param.label}
              {hint.length > 0 ? <span className="alarm-form-hint"> ({hint})</span> : null}
            </legend>
            {split ? (
              <div className="alarm-form-permode">
                {draft.enabledModes.length === 0 ? (
                  <p className="muted">Select a mode above to set thresholds.</p>
                ) : (
                  draft.enabledModes.map((m) => {
                    const errKey = `${m}::${param.key}`;
                    return (
                      <div key={m} className="alarm-form-permode-row">
                        <label htmlFor={`alarm-form-threshold-${param.key}-${m}`}>{m}</label>
                        <input
                          id={`alarm-form-threshold-${param.key}-${m}`}
                          className="form-input"
                          type="number"
                          inputMode="decimal"
                          step={units.step ?? 'any'}
                          value={modeValue(draft, m, param.key)}
                          data-testid={`alarm-form-threshold-${param.key}-${m}`}
                          onChange={(e) =>
                            onDraftChange(setThreshold(draft, m, param.key, e.target.value))
                          }
                        />
                        {units.unitSuffix.length > 0 ? (
                          <span className="alarm-form-unit">{units.unitSuffix}</span>
                        ) : null}
                        {showErrors && errors.thresholds[errKey] ? (
                          <p className="alarm-form-err">{errors.thresholds[errKey]}</p>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="alarm-form-shared-row">
                {isContactStateParam(param.key) ? (
                  // A detector contact's polarity is one of two answers, not a
                  // number (TOM-136): a 0–1 box invited 0.5.
                  <ContactStateSelect
                    id={`alarm-form-threshold-${param.key}`}
                    className="form-input"
                    value={sharedValue(draft, param.key)}
                    allowBlank={contactStateClearable(draft)}
                    testId={`alarm-form-threshold-${param.key}`}
                    onChange={(value) =>
                      onDraftChange(
                        setThreshold(
                          draft,
                          draft.enabledModes[0] ?? draft.modeUniverse[0] ?? '',
                          param.key,
                          value
                        )
                      )
                    }
                  />
                ) : (
                  <input
                    id={`alarm-form-threshold-${param.key}`}
                    className="form-input"
                    type="number"
                    inputMode="decimal"
                    step={units.step ?? 'any'}
                    value={sharedValue(draft, param.key)}
                    data-testid={`alarm-form-threshold-${param.key}`}
                    onChange={(e) =>
                      onDraftChange(
                        setThreshold(
                          draft,
                          draft.enabledModes[0] ?? draft.modeUniverse[0] ?? '',
                          param.key,
                          e.target.value
                        )
                      )
                    }
                  />
                )}
                {units.unitSuffix.length > 0 ? (
                  <span className="alarm-form-unit">{units.unitSuffix}</span>
                ) : null}
                {showErrors && errors.thresholds[`shared::${param.key}`] ? (
                  <p className="alarm-form-err">{errors.thresholds[`shared::${param.key}`]}</p>
                ) : null}
              </div>
            )}

            {/* What the alarm's own signal reads, in this box's unit — so the
                number being typed can be judged against the boat rather than
                remembered from another page. One line per setting, not per mode:
                the reading is the same signal whichever mode is being edited. */}
            {readingHint !== null ? (
              <p className="alarm-form-now" data-testid={`alarm-form-now-${param.key}`}>
                {readingHint}
              </p>
            ) : null}

            {canSplit ? (
              <>
                <label
                  className="alarm-form-mode alarm-form-split"
                  htmlFor={`alarm-form-split-${param.key}`}
                >
                  <input
                    id={`alarm-form-split-${param.key}`}
                    type="checkbox"
                    checked={split}
                    data-testid={`alarm-form-split-${param.key}`}
                    onChange={(e) =>
                      onDraftChange(setSplitByMode(draft, param.key, e.target.checked))
                    }
                  />
                  <span>Set a different value for each mode</span>
                </label>
                {/* Unchecking throws numbers away. Say which one survives BEFORE
                    the click rather than after it — the box the operator would
                    have to re-type is not recoverable. */}
                {wouldLose !== null ? (
                  <p className="alarm-form-hint" data-testid={`alarm-form-split-warn-${param.key}`}>
                    Turning this off keeps {sharedValue(draft, param.key)}
                    {units.unitSuffix.length > 0 ? ` ${units.unitSuffix}` : ''} for every mode and
                    drops {wouldLose.join(', ')}.
                  </p>
                ) : null}
              </>
            ) : null}
          </fieldset>
        );
      })}

      {/* The engine-running condition preset — offered only on the recipes whose
          library metadata declares it (the API rejects it anywhere else). Off by
          default: a condition can only make an alarm quieter. The section also
          appears with no toggle when the alarm carries clauses this form does
          not author, so they are never invisible on the screen that saves them. */}
      {draft.conditionSupport !== undefined || draft.otherConditionClauses.length > 0 ? (
        <fieldset className="alarm-form-fieldset">
          <legend>Only when</legend>
          {draft.conditionSupport !== undefined ? (
            <label className="alarm-form-mode" htmlFor="alarm-form-engine-condition">
              <input
                id="alarm-form-engine-condition"
                type="checkbox"
                checked={draft.engineCondition}
                data-testid="alarm-form-engine-condition"
                onChange={(e) => onDraftChange(setEngineCondition(draft, e.target.checked))}
              />
              <span>Only while the engine is running</span>
            </label>
          ) : null}

          {draft.engineCondition ? (
            <>
              <div className="form-group" style={{ marginTop: 10 }}>
                <label htmlFor="alarm-form-engine-minrpm">Counts as running above</label>
                <div className="alarm-form-shared-row">
                  <input
                    id="alarm-form-engine-minrpm"
                    className="form-input"
                    type="number"
                    inputMode="decimal"
                    min={1}
                    step={50}
                    value={draft.minRpm}
                    data-testid="alarm-form-engine-minrpm"
                    onChange={(e) => onDraftChange(setMinRpm(draft, e.target.value))}
                  />
                  <span className="alarm-form-unit">RPM</span>
                </div>
                {showErrors && errors.minRpm !== undefined ? (
                  <p className="alarm-form-err" data-testid="alarm-form-engine-minrpm-err">
                    {errors.minRpm}
                  </p>
                ) : null}
              </div>

              {/* Fail-closed, stated permanently: this is a safety alarm, and a
                  condition can silence it. The last sentence is the D2
                  polarity — an unreported tach arms the alarm WITHOUT the
                  condition rather than leaving the engine unwatched.
                  The "only while" claim is a NECESSITY claim, so it holds
                  whenever this clause has to be true for the alarm to fire:
                  under an `all` fold, or when it is the only clause. Under an
                  `any` fold beside other clauses the alarm can fire on one of
                  those instead, and claiming otherwise would be false. */}
              <div className="honest-note" data-testid="alarm-form-engine-failclosed">
                <InfoCircleIcon />
                {engineConditionExclusive ? (
                  <span>
                    With this on, the alarm fires only while your boat reports engine RPM above that
                    number. If the tachometer stops reporting, the condition reads false and this
                    alarm won’t fire — a broken sensor silences it too. If your boat reports no
                    engine RPM at all, we arm the alarm <strong>without</strong> the condition
                    rather than leave the engine unwatched.
                  </span>
                ) : (
                  <span>
                    With this on, the engine counts as running while your boat reports engine RPM
                    above that number. This alarm’s conditions fold with “any”, so it can still fire
                    on one of the others: if the tachometer stops reporting, this condition reads
                    false and that removes one way for the alarm to fire. If your boat reports no
                    engine RPM at all, we arm the alarm <strong>without</strong> the condition
                    rather than leave the engine unwatched.
                  </span>
                )}
              </div>
            </>
          ) : null}

          {/* Clauses this form does not author (added through the API). Shown so
              a save here never looks like it dropped them — it doesn't. */}
          {draft.otherConditionClauses.length > 0 ? (
            <p
              className="muted"
              style={{ margin: '10px 0 0' }}
              data-testid="alarm-form-other-conditions"
            >
              Set up outside this form:{' '}
              {conditionsClause({
                logic: draft.conditionLogic,
                clauses: draft.otherConditionClauses,
              })}
              . Saving here leaves it as it is.
            </p>
          ) : null}

          {showErrors && errors.conditions !== undefined ? (
            <p className="alarm-form-err" data-testid="alarm-form-conditions-err">
              {errors.conditions}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {perModeNotice !== null ? (
        <p
          className="alarm-form-permode-notice"
          role="status"
          data-testid="alarm-form-permode-notice"
        >
          Right now only your <strong>{perModeNotice.evaluatedMode}</strong> values are evaluated —{' '}
          {perModeNotice.otherModes.join(', ')}{' '}
          {perModeNotice.otherModes.length === 1 ? 'is' : 'are'} saved for later.
        </p>
      ) : null}

      {serverError !== null ? (
        <p className="alarm-command-banner" role="alert" data-testid="alarm-form-error">
          {serverError}
        </p>
      ) : null}

      <div className="alarm-form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy}
          data-testid="alarm-form-submit"
        >
          {busy ? pendingLabel : submitLabel}
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          data-testid="alarm-form-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
